import { requireFastApiAccessToken } from "@/core/fastapi";
import type {
  ThoughtGrouping,
  GenerateResumeApiServiceData,
  GenerateResumeRequest,
  SelectedProfileInput,
} from "@/generated/fastapi";
import { getDefaultAssessmentInstrumentId } from "@/shared/config/backend";
import { get_client_local_time } from "@/shared/utils";

// TODO move this to user's profile settings
const AUTOSAVE_SELECTION = false;

function formatErrorDetail(detail: unknown): string | null {
  if (!detail) return null;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((item) => formatErrorDetail(item))
      .filter(Boolean)
      .join(" ");
  }
  if (typeof detail === "object") {
    const value = detail as {
      msg?: unknown;
      message?: unknown;
      detail?: unknown;
    };
    return (
      formatErrorDetail(value.message) ??
      formatErrorDetail(value.msg) ??
      formatErrorDetail(value.detail) ??
      null
    );
  }
  return null;
}

async function readResumeError(response: Response): Promise<string> {
  const fallback = `Error ${response.status}: ${response.statusText}`;
  const contentType = response.headers.get("content-type") ?? "";

  try {
    if (contentType.includes("application/json")) {
      const body = (await response.json()) as {
        message?: unknown;
        detail?: unknown;
      };
      return (
        formatErrorDetail(body.message) ??
        formatErrorDetail(body.detail) ??
        fallback
      );
    }

    const text = await response.text();
    return text.trim() || fallback;
  } catch {
    return fallback;
  }
}

export async function generateResume({
  accessToken,
  groupingType,
  selectedProfile,
  userRequestedLinkMoveOn,
}: {
  accessToken: string | null | undefined;
  groupingType: ThoughtGrouping;
  selectedProfile: SelectedProfileInput;
  userRequestedLinkMoveOn: boolean;
}): Promise<Blob> {
  const request: GenerateResumeRequest = {
    grouping_type: groupingType,
    selected_profile: selectedProfile,
    instrument_id: userRequestedLinkMoveOn
      ? getDefaultAssessmentInstrumentId()
      : null,
    client_local_time: get_client_local_time(),
    save_selection: AUTOSAVE_SELECTION,
  };

  // TODO replace it with gensdk + parseAt = "stream". But must update library... which resolves file naming diffrently. Review later
  const servicePath =
    "/service/generate_resume" as GenerateResumeApiServiceData["url"];

  const response = await fetch(
    `${process.env.NEXT_PUBLIC_FASTAPI_URL}${servicePath}`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${requireFastApiAccessToken(accessToken)}`,
      },
      body: JSON.stringify(request),
    },
  );

  if (!response.ok) {
    throw new Error(await readResumeError(response));
  }

  return response.blob();
}
